import React from 'react'
import { createPortal } from 'react-dom'
import { CardCharge, api, newIdempotencyKey } from '../api/client'
import { formatPrice } from '../catalog/format'
import { chargeResult } from '../payments/savedCard'
import { saveErrorMessage } from '../screens/settings/toast'
import { useDialog } from '../hooks/useDialog'
import { Icon } from '../ui/Icon'
import './Sheet.css'
import './PayLinkSheet.css'

// complete: charged from her Complete step, so a booked visit is completed first.
export type ChargeTarget = { appointment_id: string; complete?: boolean } | { client_id: string; package_id: string }

interface ChargeCardSheetProps {
  target: ChargeTarget
  amountPence: number
  card: string
  // The client's first name, or '' when it isn't known.
  firstName: string
  // Whether the client has an email address, so the server emails the receipt.
  emailed: boolean
  onClose: () => void
  // onPaid runs once, when the charge succeeds, so the caller can reload.
  onPaid: () => void
  // onSendLink closes this sheet and opens the pay link sheet in its place.
  onSendLink: () => void
}

const POLL_MS = 3000

// ChargeCardSheet confirms a charge to a saved card, takes it, and shows the
// answer: paid, the bank wants the client to approve it, declined, or still
// being checked (polled every 3 s until the webhook settles it). One
// idempotency key is made when the sheet opens and used for every press, so a
// retry or a double tap can't charge twice.
export default function ChargeCardSheet({ target, amountPence, card, firstName, emailed, onClose, onPaid, onSendLink }: ChargeCardSheetProps) {
  const key = React.useRef(newIdempotencyKey()).current
  const [charge, setCharge] = React.useState<CardCharge | null>(null)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const paidReported = React.useRef(false)
  const onPaidRef = React.useRef(onPaid)
  onPaidRef.current = onPaid
  const sending = React.useRef(false)

  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, onClose, { closable: !busy })

  const apply = React.useCallback((latest: CardCharge) => {
    setCharge(latest)
    if (latest.status === 'succeeded' && !paidReported.current) {
      paidReported.current = true
      onPaidRef.current()
    }
  }, [])

  const submit = async () => {
    if (sending.current) return
    sending.current = true
    setBusy(true)
    setError('')
    try {
      apply(await api.charge(target, key, amountPence))
    } catch (e) {
      setError(saveErrorMessage(e))
    }
    sending.current = false
    setBusy(false)
  }

  const chargeId = charge?.id ?? null
  const pending = charge?.status === 'pending'
  React.useEffect(() => {
    if (!chargeId || !pending) return
    let live = true
    const timer = setInterval(() => {
      api
        .getCharge(chargeId)
        .then((latest) => {
          if (live) apply(latest)
        })
        .catch(() => {
          // Keep checking; the next tick asks again.
        })
    }, POLL_MS)
    return () => {
      live = false
      clearInterval(timer)
    }
  }, [chargeId, pending, apply])

  const result = charge ? chargeResult(charge) : null

  let body: React.ReactNode
  let footer: React.ReactNode
  if (!result) {
    body = (
      <div className="paylink">
        <p className="paylink__amount">{formatPrice(amountPence)}</p>
        <p className="paylink__status">{card}</p>
        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
      </div>
    )
    footer = (
      <>
        <div className="sheet-stack">
          <button type="button" className="btn btn-primary btn-block" onClick={submit} disabled={busy}>
            {busy ? 'Charging…' : `Charge ${formatPrice(amountPence)}`}
          </button>
          <button type="button" className="btn btn-quiet btn-block" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </div>
      </>
    )
  } else if (result.kind === 'checking') {
    body = (
      <p className="paylink__status" role="status">
        Checking…
      </p>
    )
    footer = (
      <button type="button" className="btn btn-quiet btn-block" onClick={onClose}>
        Close
      </button>
    )
  } else if (result.kind === 'paid') {
    // A confirmation, not the amount again: the tick, what was paid, and
    // the card and receipt under it.
    const receipt = emailed ? (firstName ? ` · email sent to ${firstName}` : ' · email sent') : ''
    body = (
      <div className="paylink paylink--done">
        <span className="paylink__done-icon" aria-hidden="true">
          <Icon name="check" size={32} />
        </span>
        <p className="paylink__done-title" role="status">
          Paid {formatPrice(charge!.amount_pence)}
        </p>
        <p className="paylink__status">
          {card}
          {receipt}
        </p>
      </div>
    )
    footer = (
      <div className="sheet-stack">
        <button type="button" className="btn btn-primary btn-block" onClick={onClose}>
          Done
        </button>
      </div>
    )
  } else {
    body = (
      <div className="paylink" role="alert">
        <p>{result.kind === 'needs_client' ? `${firstName || 'The client'}’s bank wants them to approve this one` : result.heading}</p>
        <p className="paylink__status">Nothing was charged.</p>
      </div>
    )
    footer = (
      <>
        <div className="sheet-stack">
          <button type="button" className="btn btn-primary btn-block" onClick={onSendLink}>
            Send pay link
          </button>
          <button type="button" className="btn btn-quiet btn-block" onClick={onClose}>
            Close
          </button>
        </div>
      </>
    )
  }

  const sheet = (
    <div
      className="sheet-overlay"
      onClick={(e) => {
        e.stopPropagation()
        if (!busy) onClose()
      }}
    >
      <div ref={dialogRef} className="sheet-content" role="dialog" aria-modal="true" aria-label="Charge card" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle"></div>
        <div className="sheet-header">
          <h2>Charge card</h2>
        </div>
        <div className="sheet-body">{body}</div>
        {footer}
      </div>
    </div>
  )

  const host = typeof document === 'undefined' ? null : document.querySelector('.app-container')
  return host ? createPortal(sheet, host) : sheet
}
